import React, { PureComponent } from 'react';

import {
  PieChart,
  Pie,
  Cell,
  Tooltip
} from "recharts";

import {
  Wrapper,
  PreTitle,
  Title,
  Text,
  ContentContainer,
  ContentTitle,
  ContentFlexColumns,
  ContentRightColumn,
  ChartDataLabel,
} from '../../../components/ui';

import DataDetails from '../../../components/data-details';

const chartColors = [
  '#81c991',
  '#ccedd1',
  '#fd9595'
]

class TeamDynamics extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      teamsWorkedVeryWellTogether: [],
      teamsWorkedWellTogether: [],
      teamsDidNotWorkWellTogether: [],
      extraDetailsTitle: 'Worked very well together',
      extraDetailsColor: chartColors[0],
      extraDetailsData: [],
    };
  }

  componentDidMount() {
    const { teams, surveysTeamScore } = this.props;

    const teamsWorkedVeryWellTogetherIDs = surveysTeamScore.collaboration ? surveysTeamScore.collaboration.team_worked_well_together : [];
    const teamsWorkedVeryWellTogether = teamsWorkedVeryWellTogetherIDs
      ? teams.filter(team => teamsWorkedVeryWellTogetherIDs.includes(team.id)).map(team =>
        `Team ${team.name}`
      )
      : [];

    const teamsWorkedWellTogetherIDs = surveysTeamScore.collaboration ? surveysTeamScore.collaboration.team_worked_together : [];
    const teamsWorkedWellTogether = teamsWorkedWellTogetherIDs
      ? teams.filter(team => teamsWorkedWellTogetherIDs.includes(team.id)).map(team =>
        `Team ${team.name}`
      )
      : [];

    const teamsDidNotWorkWellTogetherIDs = surveysTeamScore.collaboration ? surveysTeamScore.collaboration.team_did_not_worked_together : [];
    const teamsDidNotWorkWellTogether = teamsDidNotWorkWellTogetherIDs
      ? teams.filter(team => teamsDidNotWorkWellTogetherIDs.includes(team.id)).map(team =>
        `Team ${team.name}`
      )
      : [];

    const extraDetailsTitle = teamsWorkedVeryWellTogether.length
      ? 'Worked very well together'
      : teamsWorkedWellTogether.length
        ? 'Worked well together'
        : teamsDidNotWorkWellTogether.length
          ? 'Did not work well together'
          : '';
    const extraDetailsColor = teamsWorkedVeryWellTogether.length
      ? chartColors[0]
      : teamsWorkedWellTogether.length
        ? chartColors[1]
        : teamsDidNotWorkWellTogether.length
          ? chartColors[2]
          : '';
    const extraDetailsData = teamsWorkedVeryWellTogether.length
      ? teamsWorkedVeryWellTogether
      : teamsWorkedWellTogether.length
        ? teamsWorkedWellTogether
        : teamsDidNotWorkWellTogether.length
          ? teamsDidNotWorkWellTogether
          : [];

    this.setState({
      teamsWorkedVeryWellTogether,
      teamsWorkedWellTogether,
      teamsDidNotWorkWellTogether,
      extraDetailsTitle,
      extraDetailsColor,
      extraDetailsData
    });
  }

  workedPercentage(value, total) {
    return total
      ? value / total * 100
      : 0;
  }

  changeExtraDetails = (title, color, data) => {
    this.setState({
      extraDetailsTitle: title,
      extraDetailsColor: color,
      extraDetailsData: data
    });
  }

  render() {
    const {
      surveysTeamScore,
      teams,
      currentFinishedSurvey
    } = this.props;

    const {
      teamsWorkedVeryWellTogether,
      teamsWorkedWellTogether,
      teamsDidNotWorkWellTogether,
      extraDetailsTitle,
      extraDetailsColor,
      extraDetailsData
    } = this.state;

    const teamsWorkedVeryWellTogetherCount = teamsWorkedVeryWellTogether.length;
    const teamsWorkedWellTogetherCount = teamsWorkedWellTogether.length;
    const teamsDidNotWorkWellTogetherCount = teamsDidNotWorkWellTogether.length;
    const totalTeams = surveysTeamScore.collaboration ? surveysTeamScore.collaboration.total_teams : 0;

    const verbForm = teamsDidNotWorkWellTogetherCount > 1 ? 'are' : 'is';

    const chartData = [
      {
        value: teamsWorkedVeryWellTogetherCount,
        color: chartColors[0],
        title: 'Worked very well together',
        data: teamsWorkedVeryWellTogether
      },
      {
        value: teamsWorkedWellTogetherCount,
        color: chartColors[1],
        title: 'Worked well together',
        data: teamsWorkedWellTogether
      },
      {
        value: teamsDidNotWorkWellTogetherCount,
        color: chartColors[12],
        title: 'Did not work well together',
        data: teamsDidNotWorkWellTogether
      },
    ];

    return (
      <Wrapper>
        <PreTitle>Team</PreTitle>
        <Title>Team Dynamics</Title>
        <Text>
          <p><b>{teamsWorkedVeryWellTogetherCount}</b> out of <b>{teams.length}</b> teams are working very well together.</p>
          <br />
          {
            teamsDidNotWorkWellTogetherCount > 0 &&
            <p>However, there {verbForm} <b>{teamsDidNotWorkWellTogetherCount}</b> team{teamsDidNotWorkWellTogetherCount > 1 && 's'} that {verbForm} having serious challenges (i.e teammates are not getting along).  Intervening early on often resolves these team dynamics issues.</p>
          }
        </Text>
        <ContentContainer>
          <ContentTitle>{currentFinishedSurvey.name}</ContentTitle>
          <ContentFlexColumns withFloatinDetailsBlock={!!teamsDidNotWorkWellTogetherCount} withPadding={true}>
            <PieChart
              width={160}
              height={160}
              margin={{ top: 0, left: 0, bottom: 0, right: 0 }}
            >
              <Tooltip formatter={value => `${Number(value).toFixed(2)}%`} separator=": " />
              <Pie
                data={[
                  {
                    name: "Worked very well together",
                    value: this.workedPercentage(teamsWorkedVeryWellTogetherCount, totalTeams)
                  },
                  {
                    name: "Worked well together",
                    value: this.workedPercentage(teamsWorkedWellTogetherCount, totalTeams)
                  },
                  {
                    name: "Did not work well together",
                    value: this.workedPercentage(teamsDidNotWorkWellTogetherCount, totalTeams)
                  },
                ]}
                cx={80}
                cy={80}
                innerRadius="0%"
                outerRadius="100%"
                fill="#8884d8"
                paddingAngle={1}
                dataKey="value"
                startAngle={270}
                endAngle={630}
                labelLine={false}
              >
                <Cell fill={chartColors[0]} />
                <Cell fill={chartColors[1]} />
                <Cell fill={chartColors[2]} />
              </Pie>
            </PieChart>
            <ContentRightColumn>
              {
                chartData.map((item, index) => (
                  <ChartDataLabel
                    key={index}
                    color={item.color}
                    onClick={() => this.changeExtraDetails(item.title, item.color, item.data)}
                    disabled={!(item.data.length > 0)}
                  >
                    {`${item.title} (${item.value})`}
                  </ChartDataLabel>
                ))
              }
            </ContentRightColumn>
          </ContentFlexColumns>
          {
            extraDetailsData.length > 0 &&
            <DataDetails
              title={extraDetailsTitle}
              color={extraDetailsColor}
              data={extraDetailsData}
            />
          }
        </ContentContainer>
      </Wrapper>
    );
  }

}

export default TeamDynamics;



// WEBPACK FOOTER //
// ./src/instructor/components/peer-feedback/pages/dynamics/team/team-dynamics.js